import { horaAMinutos, minutosAHora, rangosSeTraslapan, fechasSeTraslapan } from './time';

export const DIAS = ['LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES'];
const MAX_DIAS = DIAS.length;

// 30 min: las horas de inicio/fin siempre caen en punto o media (07:00, 07:30, 08:00...).
const PASO_BUSQUEDA_MIN = 30;
const EPSILON = 1e-6;

// PRNG determinístico (mulberry32) para que cada combinación sea reproducible pero distinta entre sí.
function crearRng(semilla) {
  let a = semilla >>> 0;
  return function rng() {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function barajar(array, rng) {
  const copia = [...array];
  for (let i = copia.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [copia[i], copia[j]] = [copia[j], copia[i]];
  }
  return copia;
}

// Heurística "least-loaded day": entre los días disponibles, elige los k con menos carga
// acumulada hasta ahora (en vez de un patrón fijo), para repartir las materias entre toda la
// semana en lugar de amontonarlas siempre en los mismos 1-2 días.
function elegirDiasBalanceado(k, cargaPorDia, ordenDesempate) {
  if (k <= 0) return [];
  if (k > MAX_DIAS) k = MAX_DIAS;

  const candidatos = ordenDesempate.map((dia, idx) => ({
    dia,
    carga: cargaPorDia.get(dia) || 0,
    idx,
  }));
  candidatos.sort((a, b) => a.carga - b.carga || a.idx - b.idx);

  return candidatos.slice(0, k).map((c) => c.dia);
}

// Determina cuántas sesiones (k, máximo 5: Lunes a Viernes) usar y la duración (en minutos) de cada una,
// de forma que min <= duración_i <= max SIEMPRE (incluida la última), aunque eso implique no
// alcanzar exactamente duracionSemanaMin cuando sea físicamente imposible con 5 días.
// Se prefiere el MAYOR número de días viable (en vez del mínimo) para repartir la carga entre
// más días de la semana, en vez de amontonarla en 1-2 días con bloques muy largos.
function dividirEnSesiones(duracionSemanaMin, minMin, maxMin, rng) {
  let kElegido = null;
  for (let k = MAX_DIAS; k >= 1; k -= 1) {
    if (k * minMin - EPSILON <= duracionSemanaMin && duracionSemanaMin <= k * maxMin + EPSILON) {
      kElegido = k;
      break;
    }
  }

  let ajustado = false;
  let totalAUsar = duracionSemanaMin;

  if (kElegido === null) {
    ajustado = true;
    if (duracionSemanaMin < minMin) {
      // ni una sola sesión mínima cabe en el total pedido: se agenda solo una sesión mínima.
      kElegido = 1;
      totalAUsar = minMin;
    } else {
      // no alcanzan los 5 días ni al máximo por sesión: se topa a la capacidad física disponible.
      kElegido = MAX_DIAS;
      totalAUsar = Math.min(duracionSemanaMin, MAX_DIAS * maxMin);
    }
  }

  const k = kElegido;
  const duraciones = [];
  let restante = totalAUsar;

  for (let i = 0; i < k; i += 1) {
    const sesionesRestantes = k - i;
    const minParaLasDemas = minMin * (sesionesRestantes - 1);
    const maxParaLasDemas = maxMin * (sesionesRestantes - 1);
    const loEste = Math.max(minMin, restante - maxParaLasDemas);
    const hiEste = Math.min(maxMin, restante - minParaLasDemas);
    const lo = Math.min(loEste, hiEste);
    const hi = Math.max(loEste, hiEste);

    // reparto parejo (restante entre las sesiones que faltan) con un poco de variación,
    // en vez de un valor aleatorio en todo el rango [lo, hi] (que da bloques muy dispares).
    const objetivo = restante / sesionesRestantes;
    const jitter = (hi - lo) * 0.2;
    let duracion = i === k - 1 ? restante : Math.min(hi, Math.max(lo, objetivo + (rng() - 0.5) * jitter));
    // clamp SIEMPRE, incluida la última sesión: nunca debe salirse de [min, max].
    duracion = Math.min(Math.max(duracion, minMin), maxMin);
    // redondear a bloques de 30 min para que la hora fin siempre quede en punto o media.
    duracion = Math.round(duracion / PASO_BUSQUEDA_MIN) * PASO_BUSQUEDA_MIN;
    duracion = Math.min(Math.max(duracion, minMin), maxMin);

    duraciones.push(duracion);
    restante -= duracion;
  }

  const totalProgramado = duraciones.reduce((suma, d) => suma + d, 0);
  if (Math.abs(totalProgramado - duracionSemanaMin) > EPSILON) {
    ajustado = true;
  }

  return { duraciones, ajustado, totalProgramado };
}

function buscarSlotLibre({ bookings, aulaNum, dia, duracionMin, horaInicioDiaMin, horaFinDiaMin, fila }) {
  const clave = `${aulaNum}|${dia}`;
  const ocupaciones = bookings.get(clave) || [];

  for (let inicio = horaInicioDiaMin; inicio + duracionMin <= horaFinDiaMin; inicio += PASO_BUSQUEDA_MIN) {
    const fin = inicio + duracionMin;
    const hayConflicto = ocupaciones.some(
      (o) =>
        rangosSeTraslapan(inicio, fin, o.inicioMin, o.finMin) &&
        fechasSeTraslapan(fila.fechaInicio, fila.fechaFin, o.fechaInicio, o.fechaFin)
    );
    if (!hayConflicto) {
      ocupaciones.push({
        inicioMin: inicio,
        finMin: fin,
        fechaInicio: fila.fechaInicio,
        fechaFin: fila.fechaFin,
        id: fila.id,
      });
      bookings.set(clave, ocupaciones);
      return { inicio, fin };
    }
  }
  return null;
}

const PASO_GRID_MIN = 30;

// Cuadrícula de ocupación (aula x día x bloque de horario) para revisar visualmente huecos libres.
function construirGridOcupacion({ bookings, cantidadAulas, horaInicioDiaMin, horaFinDiaMin }) {
  const slots = [];
  for (let t = horaInicioDiaMin; t < horaFinDiaMin; t += PASO_GRID_MIN) slots.push(t);

  const filasGrid = [];
  for (let aulaNum = 1; aulaNum <= cantidadAulas; aulaNum += 1) {
    const filaGrid = { AULA: `Aula ${aulaNum}` };
    DIAS.forEach((dia) => {
      const ocupaciones = bookings.get(`${aulaNum}|${dia}`) || [];
      slots.forEach((t) => {
        const columna = `${dia} ${minutosAHora(t)}`;
        const ocupantes = ocupaciones.filter((o) => o.inicioMin <= t && t < o.finMin);
        filaGrid[columna] = ocupantes.length ? Array.from(new Set(ocupantes.map((o) => String(o.id)))).join(', ') : '';
      });
    });
    filasGrid.push(filaGrid);
  }
  return filasGrid;
}

function generarPropuesta({ filas, cantidadAulas, horaInicioDiaMin, horaFinDiaMin, variante }) {
  const rng = crearRng(1000 + variante * 97);
  const bookings = new Map();
  const sinAsignar = [];

  const materiasPorFila = new Map();
  filas.forEach((fila) => {
    materiasPorFila.set(fila.fila, {
      semestre: fila.semestre,
      id: fila.id,
      fechaInicio: fila.fechaInicio,
      fechaFin: fila.fechaFin,
      duracionSemana: fila.duracionSemana,
      dias: {},
    });
  });

  // heurística "largest-first": las materias con más horas semanales se acomodan primero,
  // cuando la capacidad todavía está libre; el shuffle rompe empates y varía entre combinaciones.
  const filasOrdenadas = barajar(filas, rng).sort((a, b) => b.duracionSemana - a.duracionSemana);

  const cargaPorDia = new Map(DIAS.map((dia) => [dia, 0]));
  const ordenDesempate = barajar(DIAS, rng);

  filasOrdenadas.forEach((fila) => {
    const duracionSemanaMin = fila.duracionSemana * 60;
    const minMin = fila.duracionMin * 60;
    const maxMin = fila.duracionMax * 60;

    const { duraciones, ajustado, totalProgramado } = dividirEnSesiones(duracionSemanaMin, minMin, maxMin, rng);
    const dias = elegirDiasBalanceado(duraciones.length, cargaPorDia, ordenDesempate);
    const materia = materiasPorFila.get(fila.fila);

    if (ajustado) {
      sinAsignar.push({
        semestre: fila.semestre,
        id: fila.id,
        dia: '',
        motivo: `No fue posible cubrir exactamente las ${fila.duracionSemana} h/semana solicitadas con Duración (${fila.duracionMin}h) y Duración máxima (${fila.duracionMax}h) en 5 días; se programaron ${Math.round((totalProgramado / 60) * 100) / 100} h.`,
      });
    }

    // se prefiere usar la MISMA aula toda la semana para esta materia; solo se
    // prueban otras (barajadas) como último recurso si esa aula ya no tiene cupo.
    let aulaPreferida = null;

    duraciones.forEach((duracionSesion, i) => {
      const dia = dias[i] || dias[dias.length - 1];
      const otrasAulasBarajadas = barajar(
        Array.from({ length: cantidadAulas }, (_, idx) => idx + 1).filter((n) => n !== aulaPreferida),
        rng
      );
      const ordenAulas = aulaPreferida !== null ? [aulaPreferida, ...otrasAulasBarajadas] : otrasAulasBarajadas;

      let resultado = null;
      let aulaAsignada = null;
      for (const aulaNum of ordenAulas) {
        resultado = buscarSlotLibre({
          bookings,
          aulaNum,
          dia,
          duracionMin: duracionSesion,
          horaInicioDiaMin,
          horaFinDiaMin,
          fila,
        });
        if (resultado) {
          aulaAsignada = aulaNum;
          break;
        }
      }

      // se registra la carga intentada en este día sin importar si hubo cupo o no, para que
      // las siguientes materias sigan prefiriendo los días menos saturados.
      cargaPorDia.set(dia, (cargaPorDia.get(dia) || 0) + duracionSesion);

      if (resultado) {
        if (aulaPreferida === null) aulaPreferida = aulaAsignada;
        materia.dias[dia] = {
          aula: `Aula ${aulaAsignada}`,
          horaInicio: minutosAHora(resultado.inicio),
          horaFin: minutosAHora(resultado.fin),
        };
      } else {
        sinAsignar.push({
          semestre: fila.semestre,
          id: fila.id,
          dia,
          motivo: 'No se encontró aula/horario disponible sin choques en la ventana indicada',
        });
      }
    });
  });

  const gridOcupacion = construirGridOcupacion({ bookings, cantidadAulas, horaInicioDiaMin, horaFinDiaMin });

  return {
    nombre: `Opción ${variante + 1}`,
    materias: Array.from(materiasPorFila.values()),
    sinAsignar,
    gridOcupacion,
  };
}

export function generarCombinaciones({ filas, cantidadAulas, horaInicioDia, horaFinDia, numeroCombinaciones }) {
  // se alinean a bloques de 30 min por si el usuario captura una hora "suelta" (ej. 07:10).
  const horaInicioDiaMin = Math.ceil(horaAMinutos(horaInicioDia) / PASO_BUSQUEDA_MIN) * PASO_BUSQUEDA_MIN;
  const horaFinDiaMin = Math.floor(horaAMinutos(horaFinDia) / PASO_BUSQUEDA_MIN) * PASO_BUSQUEDA_MIN;

  const propuestas = [];
  for (let v = 0; v < numeroCombinaciones; v += 1) {
    propuestas.push(generarPropuesta({ filas, cantidadAulas, horaInicioDiaMin, horaFinDiaMin, variante: v }));
  }
  return propuestas;
}
